import {InteractionEvent, Point, Rectangle} from 'pixi.js';
import {HandBackground} from './HandBackground';
import {HandCardsHolder} from './HandCardsHolder';
import {HandSlider} from './HandSlider';
import {HandSliderEvents} from './hand_slider/HandSliderEvents';
import {CardsHolderEvents} from './cards_holder/CardsHolderEvents';
import {CardEvents} from '../game_table/table_objects/stacks/card/CardEvents';
import {DynamicData} from '../../../data/DynamicData';
import {StaticData} from '../../../data/StaticData';
import {PlayerData} from '../../../data/active_data/field_config/PlayerData';
import {HoverableObject} from '../../../base/HoverableObject';
import {HandController} from '../../../controllers/HandController';
import {CardSelector} from '../../../controllers/CardSelector';
import {StackSelector} from '../../../controllers/StackSelector';
import {Card} from '../game_table/table_objects/stacks/Card';
import {SimpleRoundedRect} from '../SimpleRoundedRect';
import {HandEvents} from './HandEvents';

export class BackgroundAndCardsHolder extends HoverableObject
{
    readonly cardsHolder: HandCardsHolder;
    private readonly background: HandBackground;
    private readonly cardsMask: SimpleRoundedRect;
    private readonly slider: HandSlider;
    private onSliderMoveBindThis: (e: Event) => void;
    private onCardsChangedBindThis: (e: Event) => void;
    private onCardScaleChangedBindThis: (e: Event) => void;
    private cardsWidth: number = 0;

    constructor() {
        super();

        this.background = new HandBackground();
        this.cardsHolder = new HandCardsHolder();
        this.cardsMask = new SimpleRoundedRect();
        this.slider = new HandSlider();

        this.addChild(this.background);
        this.addChild(this.cardsHolder).y = StaticData.handPadding;
        this.addChild(this.cardsMask);
        this.addChild(this.slider).x = StaticData.handPadding;

        this.cardsMask.x = StaticData.handPadding;
        this.cardsMask.y = StaticData.handCardsMaskYOffset;

        this.onSliderMoveBindThis = this.onSliderMove.bind(this);
        this.onCardsChangedBindThis = this.onCardsChanged.bind(this);
        this.onCardScaleChangedBindThis = this.onCardScaleChanged.bind(this);
        addEventListener(HandSliderEvents.MOVE, this.onSliderMoveBindThis);
        addEventListener(CardsHolderEvents.CARDS_CHANGED, this.onCardsChangedBindThis);
        addEventListener(CardEvents.SCALE_CHANGED, this.onCardScaleChangedBindThis);

        this.cardsHolder.mask = this.cardsMask;
        this.cardsMask.interactive = this.cardsMask.interactiveChildren = false;
    }

    /**
     * Обработчик события перемещения слайдера
     * @param e - внутриигровой ивент
     */
    onSliderMove(e: Event): void {
        this.applySliderPosition();
    }

    /**
     * Обработчик события изменения количества карт в руке
     * @param e - внутриигровой ивент
     */
    onCardsChanged(e: Event): void {
        this.updateCards();
        this.updateHandEmptyTextVisibility();
        dispatchEvent(new MessageEvent(HandEvents.UPDATED));
    }

    updateHandEmptyTextVisibility(): void {
        this.background.setEmptyTextVisible(this.cardsHolder.cardsData!.length == 0);
    }

    destroy(): void {
        removeEventListener(HandSliderEvents.MOVE, this.onSliderMoveBindThis);
        removeEventListener(CardsHolderEvents.CARDS_CHANGED, this.onCardsChangedBindThis);
        removeEventListener(CardEvents.SCALE_CHANGED, this.onCardScaleChangedBindThis);
        delete this.onSliderMoveBindThis;
        delete this.onCardsChangedBindThis;
        delete this.onCardScaleChangedBindThis;

        super.destroy();
    }

    /**
     * Обработчик события изменения размера в руке
     * @param e - внутриигровой ивент
     */
    onCardScaleChanged(e: Event): void {
        this.updateCards();
    }

    resize() {
        this.background.draw(DynamicData.handSizes.width, DynamicData.handSizes.height, 0x222222, StaticData.sceneMaskRadius, 0);
        this.background.resize();
        this.updateCards();
        this.cardsMask.draw(DynamicData.handSizes.width - StaticData.handPadding * 2, DynamicData.handSizes.height - StaticData.handCardsMaskYOffset - StaticData.handPadding, 0xffffff, StaticData.sceneMaskRadius - StaticData.handPadding, 0);
        this.cardsMask.hitArea = new Rectangle();
    }

    async initialize(playerData: PlayerData): Promise<void> {
        await this.cardsHolder.initialize(playerData.cards);
        this.cardsHolder.clear();
        await this.cardsHolder.drawCards();
    }

    /**
     * Обработчик события pointerover
     * @param e Событие взаимодейтсвия пользователя
     */
    onPointerOver(e: InteractionEvent): void {
        if (DynamicData.dragSomething && CardSelector.selectedCards.length && DynamicData.holdingTempStacksCreated) {
            CardSelector.selectedCards.forEach(card => card.hideHoldingTempStack());
        }
        HandController.highlightHand();
        if (e.target instanceof Card) {
            return;
        }
        let position: Point = this.parent.toLocal(e.data.global, void 0);
        let right: boolean = position.x > DynamicData.handSizes.width / 2;
        HandController.overHandIndex = this.cardsHolder.getPlaceIndex(right);
        if (DynamicData.dragSomething) {
            if (CardSelector.selectedCards.length) {
                this.cardsHolder.tempShowDraggingCardsAtIndex(HandController.overHandIndex);
            } else if (StackSelector.selectedStacks.length) {
                this.cardsHolder.tempShowDraggingStacksAtIndex(HandController.overHandIndex);
            }
        }
    }

    /**
     * Обработчик события pointerout
     * @param e Событие взаимодейтсвия пользователя
     */
    onPointerOut(e: InteractionEvent): void {
        HandController.unhighlightHand();
        if (DynamicData.dragSomething && CardSelector.selectedCards.length) {
            if (!DynamicData.holdingTempStacksCreated) {
                CardSelector.selectedCards.forEach(card => card.createHoldingTempStack());
                DynamicData.holdingTempStacksCreated = true;
            }
            if (DynamicData.holdingTempStacksCreated) {
                CardSelector.selectedCards.forEach(card => card.showHoldingTempStack());
            }
        }
    }

    private applySliderPosition(): void {
        this.cardsHolder.x = StaticData.handPadding + this.slider.normalizedPosition * (DynamicData.handSizes.width - StaticData.handPadding * 2 - this.cardsWidth);
    }

    private updateCards(): void {
        this.cardsWidth = this.cardsHolder.setSizes(DynamicData.handSizes.width - StaticData.handPadding * 2, DynamicData.handSizes.height - StaticData.handPadding * 2);
        let sliderNeeded: boolean = this.slider.setSizes(DynamicData.handSizes.width, DynamicData.handSizes.height, this.cardsWidth);
        this.slider.visible = sliderNeeded;
        if (sliderNeeded) {
            this.slider.positionUpdate();
            this.applySliderPosition();
        } else {
            this.cardsHolder.x = StaticData.handPadding;
        }
    }
}
